import * as Redux from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { push } from 'react-router-redux';
import type { InvoiceSequenceHeader } from '~/ogm-hub';
import type { deleteInvoiceSequenceDraftArgs } from '~/Slices/InvoiceSequenceSlice';
import {
	deleteInvoiceSequenceDraft,
	fetchInvoiceSequencesForCustomer,
} from '~/Slices/InvoiceSequenceSlice';
import { LoadingStatus } from '~/Slices/schema';
import { createThunk } from '../../async-actions';
import { toastr } from '../../helpers/Toastr';

export interface SequenceListPageState {
	searchText: string;
	searchStatus: LoadingStatus;
	sequences: InvoiceSequenceHeader[];
	displayedSequences: InvoiceSequenceHeader[];
}

const initialState = {
	searchText: '',
	searchStatus: LoadingStatus.loading,
	sequences: [],
	displayedSequences: [],
};

export const loadPage = createAsyncThunk<
	InvoiceSequenceHeader[],
	number,
	{ rejectValue: string[] | Response }
>('seq-chart-list/loadPage', async (customerId: number, { dispatch }) => {
	const response = await dispatch(fetchInvoiceSequencesForCustomer(customerId));

	if (fetchInvoiceSequencesForCustomer.fulfilled.match(response)) {
		return response.payload;
	}

	const errorString = (response.payload as string[]).join(', ');
	toastr.error({ title: 'error', body: errorString });
	return [];
});

export const slice = Redux.createSlice({
	name: 'seq-chart-list',
	initialState,
	reducers: {
		pageLoadedFailure(state) {
			state.searchStatus = LoadingStatus.failure;
		},
		pageLoadedSuccess(
			state,
			action: Redux.PayloadAction<InvoiceSequenceHeader[]>
		) {
			state.searchStatus = LoadingStatus.success;
			state.sequences = action.payload;
			state.displayedSequences = action.payload;
		},
		unloadPage() {
			return initialState;
		},
		searchChanged(state, action: Redux.PayloadAction<string>) {
			state.searchText = action.payload;
			state.displayedSequences = state.sequences.filter((x) =>
				x.chartName.toUpperCase().includes(action.payload.toUpperCase())
			);
		},
		sequenceDraftDeletedPending(state) {
			state.searchStatus = LoadingStatus.loading;
		},
		sequenceDraftDeletedFailure(state) {
			state.searchStatus = LoadingStatus.failure;
		},
		sequenceDraftDeletedSuccess(state, action: Redux.PayloadAction<number>) {
			state.searchStatus = LoadingStatus.success;
			const indexToUpdate = state.sequences.findIndex(
				(x) => x.id == action.payload
			);
			state.sequences[indexToUpdate] = {
				...state.sequences[indexToUpdate],
				draftId: null,
			};
			if (state.searchText != null && state.searchText.trim() != '') {
				state.displayedSequences = state.sequences.filter((x) =>
					x.chartId.includes(state.searchText)
				);
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			loadPage.fulfilled,
			(state, action: Redux.PayloadAction<InvoiceSequenceHeader[]>) => {
				state.searchStatus = LoadingStatus.success;
				state.sequences = action.payload;
				state.displayedSequences = action.payload;
			}
		);
		builder.addCase(loadPage.pending, (state) => {
			state.searchStatus = LoadingStatus.loading;
		});
	},
});

const createNewSequence = createThunk(async (_, dispatch) => {
	dispatch(push('/invoiceSequences/new'));
});

const deleteSequenceDraft = createThunk<deleteInvoiceSequenceDraftArgs>(
	async (args, dispatch) => {
		const res = await dispatch(deleteInvoiceSequenceDraft(args));
		if (!deleteInvoiceSequenceDraft.fulfilled.match(res)) {
			const errorString = (res.payload as string[]).join(', ');
			toastr.error({ title: 'error', body: errorString });
			return;
		}

		dispatch(slice.actions.sequenceDraftDeletedSuccess(args.invoiceSequenceId));
	}
);

const asyncActions = { loadPage, deleteSequenceDraft, createNewSequence };

export const dispatchProps = {
	...slice.actions,
	...asyncActions,
};
